import React, { useState, useContext, useEffect } from "react";
import styles from "../styles/AdminTasksPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faDownload,
  faStop,
  faRefresh,
  faTasks,
  faCheck,
  faSpinner,
  faExclamationTriangle,
  faCalendarAlt,
  faUser,
  faCheckSquare,
  faSquare,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../AuthProvider";
import runnerApiDefault from "../api/runner_api";
import { useSnackbar } from "notistack";
import { formatDate } from "../helpers";

const AdminTasksPage = ({ runnerApi = runnerApiDefault }) => {
  const { keycloak } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(new Set());
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('id');
  const [orderType, setOrderType] = useState('desc');
  const [size, setSize] = useState(50);

  const fetchTasks = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const response = await runnerApi.getAllUsersTasks(keycloak.token, pageNumber, size, search, orderBy, orderType);
      setTasks(response.items || []);
      setPage(pageNumber);
      setTotalPages(response.pages || 1);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      enqueueSnackbar("Failed to fetch tasks", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak.token) {
      fetchTasks(1); // Reset to page 1 when filters change
    }
  }, [keycloak.token, search, orderBy, orderType, size]);

  const handleRefresh = () => {
    fetchTasks(page);
    setSelectedTasks(new Set());
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <FontAwesomeIcon icon={faCheck} className={styles.statusCompleted} />;
      case 'running':
      case 'pending':
        return <FontAwesomeIcon icon={faSpinner} className={styles.statusRunning} spin />;
      case 'cancelled':
      case 'failed':
        return <FontAwesomeIcon icon={faExclamationTriangle} className={styles.statusFailed} />;
      default:
        return <FontAwesomeIcon icon={faSpinner} className={styles.statusRunning} />;
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return styles.statusCompleted;
      case 'running':
      case 'pending':
        return styles.statusRunning;
      case 'cancelled':
      case 'failed':
        return styles.statusFailed;
      default:
        return styles.statusRunning;
    }
  };

  const canCancel = (status) => {
    return ['running', 'pending', 'waiting_for_input'].includes(status?.toLowerCase());
  };

  const canDownload = (status) => {
    return ['completed'].includes(status?.toLowerCase());
  };

  const handleCancelTask = async (taskId) => {
    setOperationInProgress(prev => new Set([...prev, taskId]));
    try {
      await runnerApi.cancelTask(taskId, keycloak.token);
      enqueueSnackbar("Task cancelled successfully", { variant: "success" });
      fetchTasks(page);
    } catch (error) {
      console.error("Error cancelling task:", error);
      enqueueSnackbar("Failed to cancel task", { variant: "error" });
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleDownloadResults = async (taskId, filename) => {
    setOperationInProgress(prev => new Set([...prev, taskId]));
    try {
      const response = await runnerApi.downloadTaskResults(keycloak.token, taskId);
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `task-${taskId}-results.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar("Download started", { variant: "success" });
    } catch (error) {
      console.error("Error downloading task results:", error);
      enqueueSnackbar("Failed to download task results", { variant: "error" });
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleDeleteSingle = async (taskId) => {
    setOperationInProgress(prev => new Set([...prev, taskId]));
    try {
      await runnerApi.deleteTask(keycloak.token, taskId);
      enqueueSnackbar("Task deleted successfully", { variant: "success" });
      fetchTasks(page);
    } catch (error) {
      console.error("Error deleting task:", error);
      enqueueSnackbar("Failed to delete task", { variant: "error" });
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedTasks.size === 0) return;

    try {
      const taskIds = Array.from(selectedTasks);
      await runnerApi.deleteMultipleTasks(keycloak.token, taskIds, forceDelete);
      enqueueSnackbar(`${taskIds.length} task(s) deleted successfully`, { variant: "success" });
      setSelectedTasks(new Set());
      setShowDeleteModal(false);
      fetchTasks(page);
    } catch (error) {
      console.error("Error deleting multiple tasks:", error);
      enqueueSnackbar("Failed to delete selected tasks", { variant: "error" });
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(tasks.map(task => task.id)));
    }
  };

  const formatTaskDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return formatDate(dateString);
    } catch (error) {
      return "Invalid Date";
    }
  };

  if (loading) {
    return (
      <div className={styles.adminTasksContainer}>
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminTasksContainer}>
      <div className={styles.header}>
        <h2>
          <FontAwesomeIcon icon={faTasks} /> Runner Management
        </h2>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label htmlFor="search">Status:</label>
            <select
              id="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.selectInput}
            >
              <option value="">All</option>
              <option value="PENDING">Pending</option>
              <option value="RUNNING">Running</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="FAILED">Failed</option>
              <option value="WAITING_FOR_INPUT">Waiting for Input</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="orderBy">Order by:</label>
            <select
              id="orderBy"
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              className={styles.selectInput}
            >
              <option value="id">ID</option>
              <option value="status">Status</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="orderType">Order:</label>
            <select
              id="orderType"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
              className={styles.selectInput}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="size">Page size:</label>
            <select
              id="size"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className={styles.selectInput}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
          >
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
          {selectedTasks.size > 0 && (
            <button
              className={styles.deleteMultipleButton}
              onClick={() => setShowDeleteModal(true)}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete Selected ({selectedTasks.size})
            </button>
          )}
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className={styles.noTasks}>
          <FontAwesomeIcon icon={faTasks} size="3x" />
          <h3>No tasks found</h3>
          <p>No tasks are currently available in the system.</p>
        </div>
      ) : (
        <>
          <div className={styles.tasksTable}>
            <div className={styles.tableHeader}>
              <div className={styles.selectColumn}>
                <FontAwesomeIcon
                  icon={selectedTasks.size === tasks.length ? faCheckSquare : faSquare}
                  className={styles.selectAll}
                  onClick={toggleSelectAll}
                />
              </div>
              <div className={styles.statusColumn}>Status</div>
              <div className={styles.idColumn}>Task ID</div>
              <div className={styles.clientColumn}>
                <FontAwesomeIcon icon={faUser} /> Client
              </div>
              <div className={styles.dateColumn}>
                <FontAwesomeIcon icon={faCalendarAlt} /> Created
              </div>
              <div className={styles.actionsColumn}>Actions</div>
            </div>

            {tasks.map((task) => (
              <div key={task.id} className={styles.taskRow}>
                <div className={styles.selectColumn}>
                  <FontAwesomeIcon
                    icon={selectedTasks.has(task.id) ? faCheckSquare : faSquare}
                    className={styles.selectTask}
                    onClick={() => toggleTaskSelection(task.id)}
                  />
                </div>
                <div className={styles.statusColumn}>
                  <span className={`${styles.status} ${getStatusClass(task.status)}`}>
                    {getStatusIcon(task.status)}
                    {task.status || 'Unknown'}
                  </span>
                </div>
                <div className={styles.idColumn}>
                  <code>{task.id}</code>
                </div>
                <div className={styles.clientColumn}>
                  {task.client_id || 'Unknown'}
                </div>
                <div className={styles.dateColumn}>
                  {formatTaskDate(task.created_at)}
                </div>
                <div className={styles.actionsColumn}>
                  {canCancel(task.status) && (
                    <button
                      className={styles.cancelButton}
                      onClick={() => handleCancelTask(task.id)}
                      disabled={operationInProgress.has(task.id)}
                      title="Cancel Task"
                    >
                      <FontAwesomeIcon icon={faStop} />
                    </button>
                  )}
                  {canDownload(task.status) && (
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleDownloadResults(task.id, `task-${task.id}-results.zip`)}
                      disabled={operationInProgress.has(task.id)}
                      title="Download Results"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                  )}
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDeleteSingle(task.id)}
                    disabled={operationInProgress.has(task.id)}
                    title="Delete Task"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageButton}
                onClick={() => fetchTasks(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {page} of {totalPages}
              </span>
              <button
                className={styles.pageButton}
                onClick={() => fetchTasks(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Delete Multiple Modal */}
      {showDeleteModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>
              <FontAwesomeIcon icon={faExclamationTriangle} /> Delete Selected Tasks
            </h3>
            <p>
              Are you sure you want to delete {selectedTasks.size} selected task(s)? 
              This action cannot be undone.
            </p>
            <div className={styles.forceDeleteContainer}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={forceDelete}
                  onChange={(e) => setForceDelete(e.target.checked)}
                />
                Force delete (including active tasks)
              </label>
            </div>
            <div className={styles.modalButtons}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowDeleteModal(false);
                  setForceDelete(false);
                }}
              >
                Cancel
              </button>
              <button
                className={styles.confirmDeleteButton}
                onClick={handleDeleteMultiple}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete Tasks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTasksPage;
